import React, { useState, useEffect, useCallback } from 'react';
import './Toast.css';

const explorerBaseByChain = (chainId) => {
  const id = Number(chainId);
  if (id === 1) return 'https://etherscan.io/tx/';
  if (id === 421614) return 'https://sepolia.arbiscan.io/tx/';
  if (id === 5003) return 'https://sepolia.mantlescan.xyz/tx/';
  return null;
};

const shortenHash = (hash) => {
  if (!hash || typeof hash !== 'string') return '';
  return `${hash.slice(0, 6)}...${hash.slice(-6)}`;
};

const Toast = ({ open, type = 'info', title, message, txHash, chainId, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(open);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 150); // Match animation duration
  }, [onClose]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && open && !isClosing) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, isClosing, handleClose]);

  // Reset state when open changes
  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsClosing(false);
    }
  }, [open]);

  // Prevent body scroll when toast is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const base = explorerBaseByChain(chainId);
  const href = base && txHash ? `${base}${txHash}` : null;

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  return (
    <div
      className={`toast-overlay ${isClosing ? 'closing' : ''}`}
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        className={`toast-card ${type} ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="toast-header">
          <div className="toast-title">
            {title || (type === 'success' ? 'Success' : type === 'error' ? 'Error' : 'Notice')}
          </div>
          <button
            className="toast-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="toast-body">
          {type === 'success' ? (
            <div className="toast-icon">{getIcon()}</div>
          ) : type === 'error' ? (
            <div className="toast-icon">{getIcon()}</div>
          ) : (
            <div className="toast-icon">{getIcon()}</div>
          )}
          <div className="toast-content">
            <div className="toast-message">{message}</div>
            {href && (
              <div className="toast-tx">
                <a href={href} target="_blank" rel="noreferrer">
                  ↗ View Transaction: {shortenHash(txHash)}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Toast;
